import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Menu, User, LogOut, LayoutDashboard, Bell } from 'lucide-react';

const LOGO_URL = `${process.env.PUBLIC_URL}/prasha-logo.jpeg`;

const navLinks = [
  { href: '/research', label: 'Research' },
  { href: '/courses', label: 'Courses' },
  { href: '/pricing', label: 'Pricing' },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const dynamicLinks = user?.role === 'admin'
    ? [...navLinks, { href: '/dashboard', label: 'Admin Panel' }]
    : navLinks;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-100" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
            <img src={LOGO_URL} alt="PRASHA CoRe" className="h-9 w-9 rounded-md object-contain" />
            <span className="font-bold text-lg text-[#1a5c5c] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              PRASHA CoRe
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {dynamicLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`text-sm font-medium transition-colors hover:text-[#1a5c5c] ${
                  isActive(link.href) ? 'text-[#1a5c5c]' : 'text-slate-600'
                } ${link.label === 'Admin Panel' ? 'bg-[#1a5c5c]/10 px-3 py-1 rounded-full' : ''}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop Auth */}
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 hover:bg-[#1a5c5c]/5" data-testid="user-menu-trigger">
                    <div className="h-8 w-8 rounded-full bg-[#1a5c5c] flex items-center justify-center text-white text-sm font-semibold">
                      {user.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <span className="text-sm font-medium text-slate-700">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user.name}</p>
                    <p className="text-xs text-slate-500">{user.email}</p>
                    <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded-full bg-[#1a5c5c]/10 text-[#1a5c5c] font-medium capitalize">{user.role}</span>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="nav-dashboard">
                    <LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { logout(); navigate('/'); }} data-testid="nav-logout" className="text-red-600">
                    <LogOut className="mr-2 h-4 w-4" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/auth')} className="text-slate-600 hover:text-[#1a5c5c]" data-testid="nav-login">
                  Log in
                </Button>
                <Button onClick={() => navigate('/auth?tab=signup')} className="bg-[#1a5c5c] hover:bg-[#154a4a] text-white btn-teal-glow" data-testid="nav-signup">
                  Get Started
                </Button>
              </>
            )}
          </div>

          {/* Mobile Hamburger */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 pt-12">
              <div className="flex flex-col gap-6">
                {dynamicLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setMobileOpen(false)}
                    className={`text-base font-medium ${isActive(link.href) ? 'text-[#1a5c5c]' : 'text-slate-600'}`}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="border-t pt-4">
                  {user ? (
                    <>
                      <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 text-base font-medium text-slate-700 mb-4">
                        <LayoutDashboard className="h-4 w-4" /> Dashboard
                      </Link>
                      <Button variant="outline" className="w-full text-red-600 border-red-200" onClick={() => { logout(); navigate('/'); setMobileOpen(false); }}>
                        <LogOut className="h-4 w-4 mr-2" /> Logout
                      </Button>
                    </>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <Button variant="outline" className="w-full" onClick={() => { navigate('/auth'); setMobileOpen(false); }}>Log in</Button>
                      <Button className="w-full bg-[#1a5c5c] hover:bg-[#154a4a] text-white" onClick={() => { navigate('/auth?tab=signup'); setMobileOpen(false); }}>Get Started</Button>
                    </div>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
}
